import { ReactNode } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import type { ActionItem, ActionGroup } from "@/components/ui/actions-dropdown";
import { cn } from "@/lib/utils";

interface MobileRowActionsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Title shown at the top of the sheet (typically the row's name). */
  title: ReactNode;
  /** Optional small subtitle under the title (e.g. email, bundle id). */
  subtitle?: ReactNode;
  /** Same shape as ActionsDropdown — single items or labelled groups. */
  actions: (ActionItem | ActionGroup)[];
}

/**
 * Bottom-sheet equivalent of `ActionsDropdown` for phone-sized viewports.
 * Rendered from mobile list cards via long-press or the row kebab button.
 * Each action is a generously-sized tap target (≥48px). Desktop pages keep
 * their existing dropdown — this component is only mounted on mobile.
 */
export function MobileRowActionsSheet({
  open,
  onOpenChange,
  title,
  subtitle,
  actions,
}: MobileRowActionsSheetProps) {
  const handleSelect = (item: ActionItem) => {
    if (item.disabled) return;
    // Close first so the sheet animation runs before any modal/dialog the
    // action might open. Schedule the click on a microtask so dropdown
    // state finishes unmounting.
    onOpenChange(false);
    setTimeout(() => item.onClick(), 0);
  };

  const renderItem = (item: ActionItem, key: string) => (
    <button
      key={key}
      type="button"
      disabled={item.disabled}
      onClick={() => handleSelect(item)}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm",
        "min-h-[48px] transition-colors",
        "hover:bg-accent active:bg-accent/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        item.disabled && "cursor-not-allowed opacity-50",
        item.destructive && "text-destructive"
      )}
    >
      {item.icon && (
        <item.icon
          className={cn("h-5 w-5 shrink-0", item.destructive ? "text-destructive" : "text-muted-foreground")}
          aria-hidden
        />
      )}
      <span className="flex-1">{item.label}</span>
    </button>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl border-t p-0 pb-[max(env(safe-area-inset-bottom),1rem)]"
      >
        {/* Grabber */}
        <div className="flex justify-center pt-2.5 pb-1" aria-hidden>
          <span className="h-1.5 w-10 rounded-full bg-muted-foreground/30" />
        </div>

        <SheetHeader className="px-5 pb-3 text-left">
          <SheetTitle className="truncate text-base font-semibold">{title}</SheetTitle>
          {subtitle ? (
            <SheetDescription className="truncate text-xs">{subtitle}</SheetDescription>
          ) : (
            <VisuallyHidden asChild>
              <SheetDescription>Row actions</SheetDescription>
            </VisuallyHidden>
          )}
        </SheetHeader>

        <div className="max-h-[60vh] overflow-y-auto px-2 pb-2">
          {actions.map((entry, idx) => {
            if ("items" in entry) {
              return (
                <div key={idx} className="py-1">
                  {idx > 0 && <div className="my-1 h-px bg-border" />}
                  {entry.label && (
                    <p className="px-3 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {entry.label}
                    </p>
                  )}
                  {entry.items.map((it, j) => renderItem(it, `${idx}-${j}`))}
                </div>
              );
            }
            return renderItem(entry, String(idx));
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default MobileRowActionsSheet;
